import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BottomNav } from './components/BottomNav';
import { InstallHint } from './components/InstallHint';
import { AnalyticsConsent } from './components/AnalyticsConsent';
import { ExploreAnnouncement } from './components/ExploreAnnouncement';
import { Feed } from './pages/Feed';
import { Explore } from './pages/Explore';
import { Moderation } from './pages/Moderation';
import { Welcome } from './pages/Welcome';
import { Add } from './pages/Add';
import { Saved } from './pages/Saved';
import { Profile } from './pages/Profile';
import { Connections } from './pages/Connections';
import { InvitePeople } from './pages/InvitePeople';
import { AccountSettings } from './pages/AccountSettings';
import { Contact } from './pages/Contact';
import { Connected } from './pages/Connected';
import { SignIn } from './pages/SignIn';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Invite } from './pages/Invite';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Toaster } from './components/ui/sonner';
import { stopSpeech } from './lib/speech';
import './App.css';

// Account recovery stands on its own, exactly as the invite moment does: no
// bottom nav and no consent sheet over the submit button. Analytics is also kept
// off /reset-password entirely, because that URL arrives carrying a live token.
const RECOVERY_PATHS = ['/forgot-password', '/reset-password'];

// The legal documents are read, not interacted with, so nothing is allowed to
// sit over them. Normal consent behaviour resumes everywhere else.
const LEGAL_PATHS = ['/privacy', '/terms'];

// Signed-out visitors get the welcome screen at the root; members get the Feed.
const Root = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div className="page" />;
  return isAuthenticated ? <Feed /> : <Welcome />;
};

// Primary navigation belongs to the authenticated app only. It never renders on
// signed-out states (welcome, Sign In, invite recipient screens) or while the
// session is still resolving.
const AppShell = () => {
  const { isAuthenticated, isLoading, user, acknowledgeAnnouncement } = useAuth();
  const { resolvedTheme } = useTheme();
  const { pathname } = useLocation();
  const onRecovery = RECOVERY_PATHS.includes(pathname);
  const showNav = isAuthenticated && !isLoading && !onRecovery;
  // The one-time announcement is account-level, so it is only trusted once the
  // session has been verified against the server - never from the cached user.
  // The invite moment stands on its own, exactly as the consent sheet does.
  const announcement =
    showNav && !pathname.startsWith('/invite/') ? user?.pendingAnnouncement : null;
  // Signed-out visitors load the app too, so the analytics choice is not gated on
  // the session. Four screens are left alone: the invite moment stands on its own,
  // Settings already carries the same control, and on the recommend/edit form the
  // sheet would sit over the submit button at the bottom of a scrolling page.
  const showConsent =
    !isLoading && !announcement && !pathname.startsWith('/invite/') && !onRecovery &&
    !LEGAL_PATHS.includes(pathname) &&
    pathname !== '/settings' && pathname !== '/add' && !pathname.startsWith('/edit/');

  // Never let a recommendation keep talking after you leave the screen.
  useEffect(() => stopSpeech, [pathname]);

  return (
    <div className={`app-shell ${showNav ? 'with-nav' : ''}`}>
      <main className="main-content">
        <Routes>
          {/* Stage 10: the obsolete /search, /pro, /list/:listId and /user/:userId
              routes were retired so nobody can reach a pre-Builder-Fest screen.
              Their backend endpoints and data are untouched. */}
          <Route path="/" element={<Root />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/moderation" element={<Moderation />} />
          <Route path="/add" element={<Add />} />
          <Route path="/edit/:recId" element={<Add />} />
          <Route path="/saved" element={<Saved />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/connections" element={<Connections />} />
          <Route path="/invite-people" element={<InvitePeople />} />
          <Route path="/settings" element={<AccountSettings />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/connected" element={<Connected />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:code" element={<Invite />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {showNav && <BottomNav />}
      {showNav && <InstallHint />}
      {showConsent && <AnalyticsConsent />}
      {announcement && (
        <ExploreAnnouncement
          announcementId={announcement}
          onAcknowledge={acknowledgeAnnouncement}
        />
      )}
      <Toaster position="bottom-center" offset="88px" mobileOffset="88px" theme={resolvedTheme} />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
